import { useDateQuery } from "@ls/date-module";

import { Provider as GridStateLegacyProvider } from "@ls/grid-state-module";
import { Provider as GridStateAzureProvider } from "@ls/grid-state-module-azure";

import Dialog from "@ls/disclaimer-service";
import LoadingMask from "@ls/react-loading-mask";
import ResultGrid from "components/ResultGrid";
import Selection from "components/Selection";
import TopBar from "components/TopBar";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { batch, useDispatch, useSelector, useStore } from "react-redux";
import {
  requestAnalyticsValues,
  setDefinitions as setAnalyticDefinitions,
} from "state/analytics/actions";
import {
  requestBookBasisValues,
  setDefinitions as setBookAnalyticDefinitions,
} from "state/bookAnalytics/actions";
import AnalyticDefinition from "state/analytics/AnalyticDefinition";
import BookAnalyticDefinition from "state/bookAnalytics/BookAnalyticDefinition";
import { selectDefinitions as selectAnalyticDefinitions } from "state/analytics/selectors";
import { selectDefinitions as selectBookAnalyticDefinitions } from "state/bookAnalytics/selectors";
import {
  setAppDecimal,
  setAppTitle,
  setAppNameTitle,
  setBandedRows,
  setGridFontSize,
  setGridHeaderHeight,
  setGridRowHeight,
  setGridVerticalLines,
  setHideGridSideBar,
  setLoadDataOnLaunch,
  setGridLevelLabels,
  startRequests,
  toggleDrawer,
  setShowGridLevelCount,
  setDefaultExpansion,
  setListenScenarioAppTalk,
} from "state/app/actions";
import {
  useUserScenarioOptionsEffect,
  useUserPlatformEffect,
} from "state/scenario";
import {
  selectAppDecimal,
  selectAppTitle,
  selectIsDrawerOpen,
  selectListenScenarioAppTalk,
} from "state/app/selectors";
import { setDefinitions as setBetaDefinitions } from "state/betas/actions";
import { setDefinitions as setScenarioAnalysisDefinitions } from "state/scenarioAnalysis/actions";
import {
  requestFactorPeriods,
  requestFactorTypes,
  setDefinitions as setFaaDefinitions,
} from "state/factorAttribution/actions";
import FactorAttributionDefinition from "state/factorAttribution/FactorAttributionDefinition";
import IssueCountDefinition from "state/issueCount/IssueCountDefinition";
import IssuerCountDefinition from "state/issuerCount/IssuerCountDefinition";
import RatingDefinition from "state/ratings/RatingDefinition";
import Scan2Definition from "state/scan2/Scan2Definition";
import {
  requestTraceAnalyticValues,
  requestTracePeriodValues,
} from "state/traceAnalytics/actions";
import TraceAnalyticDefinition from "state/traceAnalytics/TraceAnalyticDefinition";
import { setDefinitions as setTEDefinitions } from "state/trackingError/actions";
import { setDefinitions as setIssueCountDefinitions } from "state/issueCount/actions";
import { setDefinitions as setIssuerCountDefinitions } from "state/issuerCount/actions";
import { setDefinitions as setTraceAnalyticDefinitions } from "state/traceAnalytics/actions";
import { setDefinitions as setRatingDefinitions } from "state/ratings/actions";
import { setDefinitions as setScan2Definitions } from "state/scan2/actions";
import BetaDefinition from "state/betas/BetaDefinition";
import ScenarioAnalysisDefinition from "state/scenarioAnalysis/ScenarioAnalysisDefinition";
import { selectDefinitions as selectScenarioAnalysisDefinitions } from "state/scenarioAnalysis/selectors";
import { selectDefinitions as selectBetaDefinitions } from "state/betas/selectors";
import { selectDefinitions as selectTrackingErrorDefinitions } from "state/trackingError/selectors";
import { selectDefinitions as selectFaaDefinitions } from "state/factorAttribution/selectors";
import { selectDefinitions as selectIssueDefinitions } from "state/issueCount/selectors";
import { selectDefinitions as selectIssuerDefinitions } from "state/issuerCount/selectors";
import { selectDefinitions as selectScan2Definitions } from "state/scan2/selectors";
import { selectDefinitions as selectTraceAnalyticDefinitions } from "state/traceAnalytics/selectors";
import { selectDefinitions as selectRatingsDefinitions } from "state/ratings/selectors";
import {
  requestEntities,
  requestGroupingLevels,
  requestISBucketLevels,
} from "state/data/actions";
import { setGridStateId } from "state/grid/actions";
import { fetchScenarioEntityIds, fecthAsOfDate } from "state/scenario/actions";
import {
  selectFetchingBaseData,
  selectFetchingBaseSbsData,
  selectGridStateId,
} from "state/grid/selector";
import {
  requestSecurityAttributeOptions,
  setDefinitions as setSecurityAttributeDefinitions,
} from "state/securityAttributes/actions";
import { selectDefinitions as selectSecurityAttributeDefinitions } from "state/securityAttributes/selectors";
import SecurityAttributeDefinition from "state/securityAttributes/SecurityAttributeDefinition";
import { useGetDefaultBenchmarkEntity } from "state/selections";
import {
  commitSelections,
  setMinAccWeight,
  setMinBenchWeight,
  setSelectedAccountEntityId,
  setSelectedAsOfDate,
  setSelectedBenchmarkEntityId,
  setSelectedGroupingLevels,
  setSelectedGroupingLevelsFilters,
  setSelectedIsDefaultBenchmark,
  setSelectedIsLookthru,
  setSymbolicDate,
  setSecurityLabel,
  setScenariosValue,
  setSideBySide,
  setBenchmarkScenariosValue,
  setIsDisableBenchmarkWorkspace,
  setGroupingLevelFilterReweight,
} from "state/selections/actions";
import SelectionState from "state/selections/SelectionState";
import { selectSelectedAsOfDate } from "state/selections/selectors";
import TrackingErrorDefinition from "state/trackingError/TrackingErrorDefinition";
import { getPublicContext } from "tools";
import styles from "./App.module.css";
import { throwError } from "@ls/error-module";
import moment from "moment";

const getUrlStateId = () => {
  const publicContext = getPublicContext();
  const pathNames = window.location.pathname.split("/").filter((s) => s.length);
  if (pathNames?.length > 0) {
    const potentialStateId = decodeURIComponent(
      pathNames[pathNames.length - 1]
    );
    if (potentialStateId !== publicContext) {
      return potentialStateId;
    }
  }
};

const App = () => {
  console.log("App Rendered");
  const dispatch = useDispatch();
  const drawerOpen = useSelector(selectIsDrawerOpen);
  const analyticDefinitions = useSelector(
    selectAnalyticDefinitions(SelectionState.COMMITTED)
  );
  const bookAnalyticDefinitions = useSelector(
    selectBookAnalyticDefinitions(SelectionState.COMMITTED)
  );
  const betaDefinitions = useSelector(
    selectBetaDefinitions(SelectionState.COMMITTED)
  );
  const scenarioAnalysisDefinitions = useSelector(
    selectScenarioAnalysisDefinitions(SelectionState.COMMITTED)
  );
  const loadingData = useSelector(selectFetchingBaseData);
  const loadingDataSbs = useSelector(selectFetchingBaseSbsData);

  const appTitle = useSelector(selectAppTitle);
  const decimalValue = useSelector(selectAppDecimal);
  const gridStateId = useSelector(selectGridStateId);
  const selectedAsOfDate = useSelector(
    selectSelectedAsOfDate(SelectionState.COMMITTED)
  );
  const selectionState = useSelector(
    (state) => state.selection[SelectionState.COMMITTED]
  );
  const appState = useSelector((state) => state.app);
  const securityAttributeDefinitions = useSelector(
    selectSecurityAttributeDefinitions(SelectionState.COMMITTED)
  );
  const trackingErrorDefinitions = useSelector(
    selectTrackingErrorDefinitions(SelectionState.COMMITTED)
  );
  const faaDefinitions = useSelector(
    selectFaaDefinitions(SelectionState.COMMITTED)
  );
  const issueCountDefinitions = useSelector(
    selectIssueDefinitions(SelectionState.COMMITTED)
  );
  const issuerCountDefinitions = useSelector(
    selectIssuerDefinitions(SelectionState.COMMITTED)
  );
  const traceAnalyticDefinitions = useSelector(
    selectTraceAnalyticDefinitions(SelectionState.COMMITTED)
  );
  const scan2Definitions = useSelector(
    selectScan2Definitions(SelectionState.COMMITTED)
  );
  const ratingDefinitions = useSelector(
    selectRatingsDefinitions(SelectionState.COMMITTED)
  );
  const unCommittedSelectedAsOfDate = useSelector(
    selectSelectedAsOfDate(SelectionState.UNCOMMITTED)
  );
  const [auxStateRequest, setAuxStateRequest] = useState(null);
  const [disclaimer, setDisclaimer] = useState("false");
  const [excelDisclaimer, setExcelDisclaimer] = useState("false");

  const store = useStore();
  const getDefaultBenchmarkEntity = useGetDefaultBenchmarkEntity();

  const auxStateReqSymbolicDate = auxStateRequest?.selectionState?.symbolicDate;
  const auxStateSymbolicDateQuery = useDateQuery(auxStateReqSymbolicDate);

  const GridStateProvider =
    process.env.REACT_APP_HOST_ENV === "AZURE"
      ? GridStateAzureProvider
      : GridStateLegacyProvider;

  const onChangeStateId = useCallback(
    (gridStateId) => {
      dispatch(setGridStateId(gridStateId));
      /**
       * Update URL based on gridStateId.
       * 1) Reset URL to its base states
       * 2) Append new gridStateId to URL path, if it exists
       * 3) Don't forget any query params (location.search)
       */
      const pathArr = window.location.pathname
        .split("/")
        .filter((pathSegment) => pathSegment.length);
      const baseIdx = pathArr.findIndex(
        (pathSegment) => pathSegment === getPublicContext()
      );
      pathArr.splice(baseIdx + 1, pathArr.length); //path is now reset
      if (gridStateId) {
        pathArr.push(encodeURIComponent(gridStateId));
      }

      if (process.env.REACT_APP_IN2_ENV === "DEV" && false) {
        window.history.pushState(
          null,
          null,
          `/${encodeURIComponent(gridStateId ?? "")}`
        ); //must have leading slash
      } else {
        window.history.pushState(
          null,
          null,
          `/${pathArr.join("/")}${window.location.search}`
        ); //must have leading slash
      }
    },
    [dispatch]
  );

  const getAuxState = useCallback(() => {
    return {
      selectionState,
      appState,
      analyticDefinitions: analyticDefinitions.map((definition) =>
        definition.copy()
      ),
      bookAnalyticDefinitions: bookAnalyticDefinitions.map((definition) =>
        definition.copy()
      ),
      betaDefinitions: betaDefinitions.map((definition) => definition.copy()),
      scenarioAnalysisDefinitions: scenarioAnalysisDefinitions.map(
        (definition) => definition.copy()
      ),
      securityAttributeDefinitions: securityAttributeDefinitions.map(
        (definition) => definition.copy()
      ),
      teDefinitions: trackingErrorDefinitions.map((definition) =>
        definition.copy()
      ),
      faaDefinitions: faaDefinitions.map((definition) => definition.copy()),
      issueCountDefinitions: issueCountDefinitions.map((definition) =>
        definition.copy()
      ),
      issuerCountDefinitions: issuerCountDefinitions.map((definition) =>
        definition.copy()
      ),
      traceAnalyticDefinitions: traceAnalyticDefinitions.map((definition) =>
        definition.copy()
      ),
      scan2Definitions: scan2Definitions.map((definition) => definition.copy()),
      ratingDefinitions: ratingDefinitions,
    };
  }, [
    selectionState,
    appState,
    analyticDefinitions,
    bookAnalyticDefinitions,
    betaDefinitions,
    scenarioAnalysisDefinitions,
    securityAttributeDefinitions,
    trackingErrorDefinitions,
    faaDefinitions,
    issueCountDefinitions,
    issuerCountDefinitions,
    traceAnalyticDefinitions,
    ratingDefinitions,
    scan2Definitions,
  ]);

  /**Handle Aux state from grid state**/
  React.useEffect(() => {
    if (auxStateRequest) {
      let asOfDate;
      if (auxStateReqSymbolicDate?.length) {
        //if aux date has symbolic date, grab the resolved date from the symbolic date query
        asOfDate = auxStateSymbolicDateQuery?.data;
      } else {
        asOfDate = auxStateRequest.selectionState.asOfDate;
      }

      //as of date maybe null if waiting on symbolic date.
      if (!asOfDate) return;

      const {
        selectionState,
        appState,
        analyticDefinitions = [],
        bookAnalyticDefinitions = [],
        betaDefinitions = [],
        scenarioAnalysisDefinitions = [],
        securityAttributeDefinitions = [],
        teDefinitions = [],
        faaDefinitions = [],
        issueCountDefinitions = [],
        issuerCountDefinitions = [],
        traceAnalyticDefinitions = [],
        ratingDefinitions = [],
        scan2Definitions = [],
      } = auxStateRequest;
      const {
        symbolicDate,
        isLookthru,
        groupingLevels,
        groupingLevelsFilters = [],
        accountEntityId,
        benchmarkEntityId,
        isDefaultBenchmark,
        minAccWeight,
        minBenchWeight,
        securityLabel,
        scenario,
        benchmarkScenario,
        groupingLevelsFiltersReweight,
      } = selectionState;
      batch(async () => {
        dispatch(setAppTitle(appState.appTitle ?? "Portfolio Analytics"));
        dispatch(setAppNameTitle(appState.appNameTitle));
        dispatch(setDefaultExpansion(appState.defaultExpansion ?? 2));
        dispatch(setAppDecimal(appState.appDecimal ?? 2));
        dispatch(setBandedRows(appState.bandedRows ?? true));
        dispatch(setGridVerticalLines(appState.gridVerticalLines ?? true));
        dispatch(setHideGridSideBar(appState.hideGridSideBar ?? false));
        dispatch(setGridFontSize(appState.gridFontSize ?? 12));
        dispatch(setGridRowHeight(appState.gridRowHeight ?? 22));
        dispatch(setGridHeaderHeight(appState.gridHeaderHeight ?? 65));
        dispatch(setSymbolicDate(symbolicDate ?? null));
        dispatch(setSelectedAsOfDate(asOfDate));
        dispatch(setMinAccWeight(minAccWeight));
        dispatch(setMinBenchWeight(minBenchWeight));
        dispatch(setSelectedGroupingLevels(groupingLevels));
        dispatch(setSelectedGroupingLevelsFilters(groupingLevelsFilters));
        dispatch(setSelectedIsLookthru(isLookthru));
        dispatch(setSecurityLabel(securityLabel));
        dispatch(
          setAnalyticDefinitions(
            analyticDefinitions.map((def) => new AnalyticDefinition(def))
          )
        );
        dispatch(
          setBookAnalyticDefinitions(
            bookAnalyticDefinitions.map(
              (def) => new BookAnalyticDefinition(def)
            )
          )
        );
        dispatch(
          setBetaDefinitions(
            betaDefinitions.map((def) => new BetaDefinition(def))
          )
        );

        dispatch(
          setScenarioAnalysisDefinitions(
            scenarioAnalysisDefinitions.map(
              (def) => new ScenarioAnalysisDefinition(def)
            )
          )
        );

        dispatch(
          setSecurityAttributeDefinitions(
            securityAttributeDefinitions.map(
              (def) => new SecurityAttributeDefinition(def)
            )
          )
        );
        dispatch(
          setTEDefinitions(
            teDefinitions.map((def) => new TrackingErrorDefinition(def))
          )
        );
        dispatch(
          setFaaDefinitions(
            faaDefinitions.map((def) => new FactorAttributionDefinition(def))
          )
        );
        dispatch(
          setIssueCountDefinitions(
            issueCountDefinitions.map((def) => new IssueCountDefinition(def))
          )
        );
        dispatch(
          setIssuerCountDefinitions(
            issuerCountDefinitions.map((def) => new IssuerCountDefinition(def))
          )
        );
        dispatch(
          setTraceAnalyticDefinitions(
            traceAnalyticDefinitions.map(
              (def) => new TraceAnalyticDefinition(def)
            )
          )
        );
        dispatch(
          setRatingDefinitions(
            ratingDefinitions.map((def) => new RatingDefinition(def))
          )
        );
        dispatch(
          setScan2Definitions(
            scan2Definitions.map((def) => new Scan2Definition(def))
          )
        );
        const loadDataOnLaunch = appState.loadDataOnLaunch ?? true;
        dispatch(setLoadDataOnLaunch(loadDataOnLaunch));
        dispatch(setGridLevelLabels(appState.gridLevelLabels ?? true));
        dispatch(setShowGridLevelCount(appState.showGridLevelCount ?? true));
        dispatch(toggleDrawer({ override: false }));
        //dispatch(setSideBySide(sideBySide));
        dispatch(setScenariosValue(scenario));
        dispatch(setSideBySide(selectionState.sideBySide ?? false));
        dispatch(setBenchmarkScenariosValue(benchmarkScenario));
        dispatch(
          setIsDisableBenchmarkWorkspace(
            selectionState.isDisableBenchmarkWorkspace ?? true
          )
        );
        dispatch(
          setListenScenarioAppTalk(appState.listenScenarioAppTalk ?? false)
        );
        dispatch(setGroupingLevelFilterReweight(groupingLevelsFiltersReweight));
        if (loadDataOnLaunch) {
          dispatch(setSelectedAccountEntityId(accountEntityId));
          dispatch(setSelectedBenchmarkEntityId(benchmarkEntityId));
          dispatch(setSelectedIsDefaultBenchmark(isDefaultBenchmark));
          dispatch(commitSelections());
          dispatch(startRequests());
        } else {
          dispatch(commitSelections());
        }
      });
    }
  }, [
    auxStateReqSymbolicDate?.length,
    auxStateRequest,
    auxStateSymbolicDateQuery?.data,
    dispatch,
  ]);
  useUserPlatformEffect();
  useUserScenarioOptionsEffect();
  let listenScenarioAppTalk = useSelector(selectListenScenarioAppTalk);

  /**
   * For getting share url from the list of available grid states.
   */
  const getShareUrl = useCallback((gridStateId) => {
    const pathArr = window.location.pathname
      .split("/")
      .filter((pathSegment) => pathSegment.length);
    const baseIdx = pathArr.findIndex(
      (pathSegment) => pathSegment === getPublicContext()
    );
    pathArr.splice(baseIdx + 1, pathArr.length); //path is now reset to x/y/z/accountsummaryui
    if (gridStateId) {
      pathArr.push(encodeURIComponent(gridStateId));
    }
    return `${window.location.origin}/${pathArr.join("/")}${
      window.location.search
    }`;
  }, []);

  /**
   * AppTalk
   */

  useEffect(() => {
    const { in2AppTalk } = window;
    if (!in2AppTalk) {
      return;
    }
    if (!listenScenarioAppTalk) {
      return;
    }

    const instance = in2AppTalk.getInstance();

    instance.subscribe("*", (subject, msg) => {
      console.log("Received: " + subject + " " + msg.toString());

      if (
        subject === in2AppTalk.MSG_TYPE.PRIMARY_STACKED_ITEM_CHANGE &&
        msg.get("gridLoadedWithPersistedScenario")
      ) {
        //load scenario and merge scenario case
        const workspaceName = JSON.parse(
          msg.get("gridLoadedWithPersistedScenario")
        );

        //dispatch(useUserScenarioOptionsEffect());
        batch(async () => {
          if (!!workspaceName) {
            //useUserPlatformEffect();
            //useUserScenarioOptionsEffect();

            const entityIds = await dispatch(
              fetchScenarioEntityIds(workspaceName.name)
            );
            const scenarioDate = await dispatch(
              fecthAsOfDate(workspaceName.name)
            );
            const asOfDate =
              !!scenarioDate && moment(scenarioDate).format("YYYY-MM-DD");
            if (entityIds) {
              dispatch(setSelectedAccountEntityId(entityIds?.[0]));

              dispatch(setSymbolicDate(asOfDate ?? null));

              const defaultBenchmarkEntity =
                getDefaultBenchmarkEntity(entityIds);
              if (defaultBenchmarkEntity) {
                dispatch(
                  setSelectedBenchmarkEntityId(defaultBenchmarkEntity.entityId)
                );
              } else {
                dispatch(setSelectedBenchmarkEntityId(null));
              }
            } else if (!entityIds?.length) {
              throwError(
                `Unable to find any entity ids for scenario ${workspaceName.name}`
              );
              return;
            }
            dispatch(setScenariosValue(workspaceName.name));
            !!asOfDate && dispatch(setSelectedAsOfDate(asOfDate));
            if (!drawerOpen && (selectedAsOfDate || asOfDate)) {
              dispatch(commitSelections());
              dispatch(startRequests());
            }
          }
        });
      } else if (
        subject === in2AppTalk.MSG_TYPE.PRIMARY_STACKED_ITEM_CHANGE &&
        msg.get("scenarioLoaded")
      ) {
        //create new Scenario
        const scenarioLoadedData = JSON.parse(msg.get("scenarioLoaded"));
        //example msg Format below:
        // LOAD_USER_SCENARIO	TRS_TUHEEN_03
        // scenarioLoaded	{"name":"TRS_TUHEEN_03","entityIds":"539272,554570,551821,533711,554383,555968,552194,539010,545923,539908,555847,539224,533401,553690,558427,539742,533535,539346,555924,539926,540502,536599,554473,555821,533741,554478,554592,533986,533603,551142,553831,555879,534715,534205,539312,536691,539892,539126,536439"}
        console.log("create Scenario ", scenarioLoadedData);
      }
    });

    return () => {
      if (in2AppTalk) {
        instance.unsubscribe("*");
      }
    };
  }, [
    listenScenarioAppTalk,
    dispatch,
    getDefaultBenchmarkEntity,
    store,
    selectedAsOfDate,
    drawerOpen,
  ]);

  useEffect(() => {
    const { in2AppTalk } = window;
    if (!in2AppTalk) {
      return;
    }

    const instance = in2AppTalk.getInstance();

    instance.subscribe("*", (sub, msg) => {
      if (typeof msg?.get !== "function") {
        return;
      }
      console.log("In2 msg", msg);

      const entityId =
        msg.get("ACCOUNT_FIDR_ENTITY_ID") ??
        msg.get("ACCOUNT_ENTITY_ID") ??
        msg.get("COMPOSITE_ENTITY_ID") ??
        msg.get("GROUP_ACCOUNT_ENTITY_ID") ??
        msg.get("ACCOUNT_SSI_ID") ??
        msg.get("uniqueIdVal") ??
        msg.get("BENCHMARK_ENTITY_ID");

      if (entityId && !msg.get("SECURITY_NAME")) {
        //we want to ignore security in2 messages
        const accEntityId = Number(entityId);
        dispatch(setSelectedAccountEntityId(accEntityId));
        const defaultBenchmarkEntity = getDefaultBenchmarkEntity(accEntityId);
        if (defaultBenchmarkEntity) {
          dispatch(
            setSelectedBenchmarkEntityId(defaultBenchmarkEntity.entityId)
          );
        } else {
          dispatch(setSelectedBenchmarkEntityId(null));
        }

        if (!drawerOpen && selectedAsOfDate) {
          dispatch(commitSelections());
          dispatch(startRequests());
        }
      }
    });
    return () => {
      if (in2AppTalk) {
        instance.unsubscribe("*");
      }
    };
  }, [
    getDefaultBenchmarkEntity,
    dispatch,
    store,
    selectedAsOfDate,
    drawerOpen,
  ]);

  /**
  /**
   * ensures doc title is updated
   */
  useEffect(() => {
    window.document.title = appTitle;
  }, [appTitle]);

  /**
   * ensures URL state ID is applied,
   * if no url state ID is present, date is set to default PBD
   */
  useEffect(() => {
    const urlStateId = getUrlStateId();
    const disclosure = new URLSearchParams(window.location.search).get(
      "disclosure"
    );
    setDisclaimer(disclosure);
    setExcelDisclaimer(disclosure);
    if (urlStateId) {
      dispatch(setGridStateId(urlStateId));
    } else {
      dispatch(setSymbolicDate("PBD"));
    }
  }, [dispatch]);

  /**
   * complimentary data side effects
   */
  useEffect(() => {
    dispatch(requestGroupingLevels());
    dispatch(requestISBucketLevels());
    dispatch(requestAnalyticsValues());
    dispatch(requestBookBasisValues());
    dispatch(requestSecurityAttributeOptions());
    dispatch(requestFactorTypes());
    dispatch(requestFactorPeriods());
    dispatch(requestTraceAnalyticValues());
    dispatch(requestTracePeriodValues());
  }, [dispatch]);

  /**
   * Ensures entities are re-fetched when date is changed
   */
  useEffect(() => {
    if (unCommittedSelectedAsOfDate) {
      dispatch(requestEntities(unCommittedSelectedAsOfDate));
    }
  }, [unCommittedSelectedAsOfDate, dispatch]);

  /**
   * Loading mask/message
   */
  const [loading, showSpinner, loadingMessage] = useMemo(() => {
    if (auxStateSymbolicDateQuery.isFetching) {
      return [true, true, `Please wait...`];
    }
    if (loadingData) {
      return [true, true, `Please wait...`];
    }
    if (loadingDataSbs) {
      return [true, true, `Please wait...`];
    }
    return [false];
  }, [auxStateSymbolicDateQuery.isFetching, loadingData, loadingDataSbs]);
  const disclosureAccpt = () => {
    setDisclaimer("false");
  };
  return (
    <>
      {disclaimer === "true" ? (
        <Dialog acceptDisclaimer={disclosureAccpt} active={disclaimer} />
      ) : (
        <>
          <GridStateProvider
            appId={process.env.REACT_APP_ID}
            defaultDecimalValue={decimalValue}
            stateId={gridStateId}
            onChangeStateId={onChangeStateId}
            environment={process.env.REACT_APP_IN2_ENV}
            getAuxState={getAuxState}
            setAuxState={setAuxStateRequest}
            getShareUrl={getShareUrl}
            baseURL={
              process.env.REACT_APP_HOST_ENV === "AZURE"
                ? process.env.REACT_APP_BASE_URL_GRID_STATE_SERVICE
                : undefined
            }
          >
            <div className={styles.app}>
              <Selection />
              <div className={styles.top}>
                <TopBar />
              </div>
              <div className={styles.content}>
                <ResultGrid exlDisclaimer={excelDisclaimer} />
              </div>
              <LoadingMask
                active={loading}
                zIndex={12}
                msg={loadingMessage}
                showMsg
                showSpinner={showSpinner}
              />
            </div>
          </GridStateProvider>
        </>
      )}
    </>
  );
};

export default App;
